import { useMemo, useState } from "react";
import GameShell from "../GameShell";

/**
 * Labirinto dos Padrões — Raven matrices inspired.
 * 6 fixed trials. 2x2 visual grid, last cell missing.
 * Show 4 options. Pick the correct one.
 */
export default function PatternLabyrinth() {
  return (
    <GameShell gameId="pattern_labyrinth" title="Labirinto dos Padrões">
      {({ onFinish }) => <Game onFinish={onFinish} />}
    </GameShell>
  );
}

const TRIALS = [
  { grid: ["🔴", "🔵", "🔴", "?"], answer: "🔵", distractors: ["🔴", "🟢", "🟡"] },
  { grid: ["⬆️", "⬇️", "⬇️", "?"], answer: "⬆️", distractors: ["⬇️", "➡️", "⬅️"] },
  { grid: ["🌑", "🌓", "🌕", "?"], answer: "🌗", distractors: ["🌑", "🌕", "🌓"] },
  { grid: ["1", "2", "3", "?"], answer: "4", distractors: ["1", "5", "3"] },
  { grid: ["🐱", "🐶", "🐱", "?"], answer: "🐶", distractors: ["🐯", "🐱", "🐻"] },
  { grid: ["▲", "■", "▲", "?"], answer: "■", distractors: ["▲", "●", "★"] },
];

function Game({ onFinish }) {
  const [trialIdx, setTrialIdx] = useState(0);
  const [hits, setHits] = useState(0);
  const [misses, setMisses] = useState(0);
  const [reactionTimes, setReactionTimes] = useState([]);
  const [trialStart, setTrialStart] = useState(() => performance.now());
  const [started] = useState(() => performance.now());
  const [feedback, setFeedback] = useState(null);

  const trial = TRIALS[trialIdx];
  const optionsList = useMemo(
    () => [trial.answer, ...trial.distractors].sort(() => Math.random() - 0.5),
    [trialIdx] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const choose = (opt) => {
    if (feedback) return;
    const rt = performance.now() - trialStart;
    const correct = opt === trial.answer;
    setReactionTimes((r) => [...r, rt]);
    if (correct) setHits((h) => h + 1);
    else setMisses((m) => m + 1);
    setFeedback(correct ? "✅" : "❌");
    setTimeout(() => {
      const next = trialIdx + 1;
      if (next >= TRIALS.length) {
        const elapsed = performance.now() - started;
        const totalHits = hits + (correct ? 1 : 0);
        const totalMisses = misses + (correct ? 0 : 1);
        const allRts = [...reactionTimes, rt];
        const avg = allRts.reduce((a, b) => a + b, 0) / allRts.length;
        onFinish({
          score: Math.max(0, Math.round(totalHits * 15 - totalMisses * 4)),
          duration_ms: Math.round(elapsed),
          metrics: {
            hits: totalHits,
            misses: totalMisses,
            trials: TRIALS.length,
            avg_reaction_ms: Math.round(avg),
            accuracy: Number((totalHits / TRIALS.length).toFixed(3)),
          },
        });
      } else {
        setFeedback(null);
        setTrialIdx(next);
        setTrialStart(performance.now());
      }
    }, 500);
  };

  return (
    <div className="mt-4 max-w-2xl mx-auto" data-testid="pattern-area">
      <div className="text-center text-white drop-shadow mb-4">
        <div className="text-sm">Qual figura completa o padrão?</div>
        <div className="text-xs mt-1 opacity-80">Rodada {trialIdx + 1} / {TRIALS.length}</div>
      </div>

      <div className="kid-card pop-in" key={trialIdx}>
        <div className="grid grid-cols-2 gap-2 max-w-xs mx-auto mb-4">
          {trial.grid.map((cell, i) => (
            <div
              key={i}
              className={`aspect-square rounded-xl flex items-center justify-center text-5xl border-2 ${
                cell === "?" ? "border-dashed border-amber-400 bg-amber-50" : "border-slate-200 bg-white"
              }`}
            >
              {cell === "?" ? "?" : cell}
            </div>
          ))}
        </div>
        <div className="text-center text-sm text-slate-500 mb-3">Escolha:</div>
        <div className="grid grid-cols-4 gap-2">
          {optionsList.map((opt, i) => (
            <button
              key={i}
              onClick={() => choose(opt)}
              className="aspect-square rounded-xl border-2 border-slate-200 text-4xl hover:scale-105 active:scale-95 transition bg-sky-50"
              data-testid={`pattern-opt-${i}`}
            >
              {opt}
            </button>
          ))}
        </div>
        {feedback && (
          <div className="text-center text-4xl mt-3 pop-in" data-testid="pattern-feedback">
            {feedback}
          </div>
        )}
      </div>
    </div>
  );
}
